import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Chip,
  Stack,
  IconButton,
  Box,
  MenuItem,
  alpha,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import dayjs from 'dayjs';
import {
  STATUS_LABELS,
  CREATE_TIME_STATUS_OPTIONS,
} from '../../constants/status';
import { TYPE_LABELS, TYPE_OPTIONS } from '../../constants/type';

// หัวข้อกลุ่มฟิลด์ — ไอคอนในวงกลมสี tint จาง + เส้นใต้บางๆ แทน overline+Divider เดิม
// ให้แต่ละกลุ่มมีเอกลักษณ์ชัดขึ้น (เลียนแบบ SectionCard accent ของหน้ารายละเอียดโอกาสฯ)
function SectionHeader({ label, icon }) {
  return (
    <Grid item xs={12}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ mb: 0.5 }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
            '& svg': { fontSize: 16 },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, letterSpacing: 0.2 }}
        >
          {label}
        </Typography>
      </Stack>
      <Box
        sx={{
          height: 2,
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          mb: 1,
        }}
      />
    </Grid>
  );
}

// clientField/ownerField: node จาก Autocomplete แยกไฟล์ — วางแทรกกลาง Grid เดียวกัน
// เพื่อให้ตำแหน่ง/การ wrap ของ layout เดิมยังเหมือนเดิมทุกประการ
//
// เลย์เอาต์ 2 คอลัมน์ (กันต้อง scroll ขึ้นลง — โมดัลกว้างขึ้นแทน ดู maxWidth ที่
// OpportunityFormModal.js): ซ้าย = เลขที่เอกสาร + ข้อมูลโครงการ (กลุ่มที่ต้องกรอกก่อน/
// ระบุตัวตนโครงการ), ขวา = งบประมาณ/กำหนดการ + หมายเหตุ/โน้ต (กลุ่มรายละเอียดประกอบ) —
// สองคอลัมน์สูงใกล้เคียงกันโดยเจตนา (แต่ละคอลัมน์มี ~5 ช่อง + textarea 1-2 ช่อง)
export function OpportunityFormFields({
  form,
  onFieldChange,
  clientField,
  ownerField,
  orderedByField,
  isEdit,
  appointments = [],
  newAppointmentDate,
  onNewAppointmentDateChange,
  newAppointmentTitle,
  onNewAppointmentTitleChange,
  onAddAppointment,
  onRemoveAppointment,
}) {
  const codeInputRefs = React.useRef([]);
  const documentInputRefs = React.useRef([]);
  // เตือนเมื่อพิมพ์ตัวอักษรที่ไม่ใช่ภาษาอังกฤษเข้าช่อง Code — เช็คจากค่าที่พิมพ์ดิบก่อนกรอง
  // ทิ้ง (โค้ดกรองทิ้งเงียบๆ อยู่แล้ว แต่ user ควรรู้ว่าทำไมตัวอักษรที่พิมพ์ไปไม่ขึ้น)
  const [codeInvalidChar, setCodeInvalidChar] = React.useState(false);

  return (
    <Grid
      container
      spacing={{ xs: 2, md: 3 }}
      alignItems="stretch"
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
    >
      {/* คอลัมน์ซ้าย: เลขที่เอกสาร + ข้อมูลโครงการ */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          '& > .MuiGrid-container': { width: '100%' },
        }}
      >
        <Grid
          container
          spacing={2.5}
          sx={{
            alignContent: 'flex-start',
            p: { xs: 2, md: 2.5 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          <SectionHeader label="เลขที่เอกสาร" icon={<TagOutlinedIcon />} />
          {isEdit ? (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="เลขที่โครงการ (Document No.)"
                value={form.opportunityNumber}
                disabled
                helperText="ตั้งครั้งเดียวตอนสร้าง แก้ไขไม่ได้"
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
                >
                  Code * (2 ตัว เช่น MS)
                </Typography>
                <Stack direction="row" spacing={0.75}>
                  {Array.from({ length: 2 }, (_, index) => (
                    <TextField
                      key={`code-${index}`}
                      size="small"
                      inputRef={(element) => {
                        codeInputRefs.current[index] = element;
                      }}
                      value={form.code[index] || ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setCodeInvalidChar(/[^A-Za-z]/.test(raw));
                        const value = raw
                          .toUpperCase()
                          .replace(/[^A-Z]/g, '')
                          .slice(-1);
                        const code = form.code.split('');
                        code[index] = value;
                        onFieldChange('code', code.join('').slice(0, 2));
                        if (value && index < 1) {
                          codeInputRefs.current[index + 1]?.focus();
                        }
                      }}
                      inputProps={{
                        maxLength: 1,
                        'aria-label': `Code ตัวที่ ${index + 1}`,
                        style: {
                          textAlign: 'center',
                          textTransform: 'uppercase',
                        },
                      }}
                      sx={{ width: { xs: 42, sm: 48 } }}
                    />
                  ))}
                </Stack>
                {codeInvalidChar && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.5, color: 'error.main' }}
                  >
                    กรุณาพิมพ์เป็นภาษาอังกฤษเท่านั้น
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
                >
                  เลขที่เอกสาร * (4 หลัก เช่น 0035)
                </Typography>
                <Stack direction="row" spacing={0.75}>
                  {Array.from({ length: 4 }, (_, index) => (
                    <TextField
                      key={`document-${index}`}
                      size="small"
                      inputRef={(element) => {
                        documentInputRefs.current[index] = element;
                      }}
                      value={form.xxx[index] || ''}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/[^0-9]/g, '')
                          .slice(-1);
                        const documentNumber = form.xxx.split('');
                        documentNumber[index] = value;
                        onFieldChange(
                          'xxx',
                          documentNumber.join('').slice(0, 4),
                        );
                        if (value && index < 3) {
                          documentInputRefs.current[index + 1]?.focus();
                        }
                      }}
                      inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        'aria-label': `เลขที่เอกสารตัวที่ ${index + 1}`,
                        style: { textAlign: 'center' },
                      }}
                      sx={{ width: { xs: 38, sm: 44 } }}
                    />
                  ))}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}
                >
                  {form.code && form.xxx
                    ? `Document No.: ${form.code}_${dayjs().format('YY')}${
                        form.xxx
                      }`
                    : 'Document No. = Code + _ + ปี + เลขนี้'}
                </Typography>
              </Grid>
            </>
          )}

          <SectionHeader label="ข้อมูลโครงการ" icon={<InfoOutlinedIcon />} />
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="ชื่อโครงการ *"
              value={form.projectName}
              onChange={(e) => onFieldChange('projectName', e.target.value)}
            />
          </Grid>
          {clientField}
          {ownerField}
          {orderedByField}
          <Grid item xs={12} sm={form.type === 'OTHER' ? 6 : 12}>
            <TextField
              fullWidth
              size="small"
              select
              label="Type"
              value={form.type}
              onChange={(e) => onFieldChange('type', e.target.value)}
            >
              <MenuItem value="">ยังไม่ระบุ</MenuItem>
              {TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {form.type === 'OTHER' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="ระบุประเภทงาน (Other) *"
                value={form.typeOther}
                onChange={(e) => onFieldChange('typeOther', e.target.value)}
              />
            </Grid>
          )}
          {!isEdit && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                select
                label="สถานะ"
                value={form.status}
                onChange={(e) => onFieldChange('status', e.target.value)}
              >
                {CREATE_TIME_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {/* วันที่เริ่ม (= วันที่สร้างโครงการ) และวันที่เสร็จ (= วันที่กดจบงาน สถานะ WON/LOST)
              เป็นค่าที่ระบบตั้งเองเสมอ ไม่ให้กรอกในฟอร์มนี้ — ดูได้ที่หน้ารายละเอียดโครงการ
              มูลค่างาน/เลขที่ PO ย้ายไปกรอกตอน "จบงาน" แทน (ดู FinishJobDialog) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="รายละเอียด"
              multiline
              rows={2}
              value={form.detail}
              onChange={(e) => onFieldChange('detail', e.target.value)}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* คอลัมน์ขวา: งบประมาณและกำหนดการ + หมายเหตุ/โน้ต */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{ display: 'flex', '& > .MuiGrid-container': { width: '100%' } }}
      >
        <Grid
          container
          spacing={2.5}
          sx={{
            alignContent: 'flex-start',
            p: { xs: 2, md: 2.5 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          <SectionHeader
            label="งบประมาณและกำหนดการ"
            icon={<PaymentsOutlinedIcon />}
          />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="วันครบกำหนดยื่นใบเสนอราคา"
              type="date"
              value={form.quotationDueDate}
              onChange={(e) =>
                onFieldChange('quotationDueDate', e.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="งบประมาณ (Budget)"
              type="number"
              value={form.budget}
              onChange={(e) => onFieldChange('budget', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{ mb: 1 }}
              >
                <EventOutlinedIcon
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  วันที่นัด
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                sx={{ mb: appointments.length ? 1.25 : 0.5, gap: 0.75 }}
              >
                {appointments.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    ยังไม่มีวันที่นัด
                  </Typography>
                )}
                {appointments.map((a) => (
                  <Chip
                    key={a.id || a.date}
                    label={
                      a.title
                        ? `${dayjs(a.date).format('DD/MM/YYYY')} — ${a.title}`
                        : dayjs(a.date).format('DD/MM/YYYY')
                    }
                    onDelete={() => onRemoveAppointment(a.id)}
                    deleteIcon={<DeleteOutlineIcon />}
                    size="small"
                    sx={{ bgcolor: 'background.paper' }}
                  />
                ))}
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  size="small"
                  type="date"
                  value={newAppointmentDate}
                  onChange={(e) => onNewAppointmentDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="เพิ่มได้หลายวัน กดบวกทีละวัน"
                  sx={{
                    flex: '1 1 170px',
                    minWidth: { xs: '100%', sm: 170 },
                    borderRadius: 2,
                  }}
                />
                <TextField
                  size="small"
                  label="นัดเรื่องอะไร"
                  placeholder="เช่น นัดพบลูกค้า, ยื่นซองประมูล"
                  value={newAppointmentTitle}
                  onChange={(e) => onNewAppointmentTitleChange(e.target.value)}
                  sx={{
                    flex: '2 1 220px',
                    minWidth: { xs: '100%', sm: 220 },
                    borderRadius: 2,
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={onAddAppointment}
                  disabled={!newAppointmentDate}
                  sx={{
                    mt: { xs: 0, sm: 0.5 },
                    alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>

          {/* กลุ่ม: หมายเหตุ / โน้ต — คนละช่องกัน ไม่ใช่ฟิลด์เดียวกัน */}
          <SectionHeader label="หมายเหตุ / โน้ต" icon={<NotesOutlinedIcon />} />
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="หมายเหตุ"
              multiline
              rows={2}
              value={form.remark}
              onChange={(e) => onFieldChange('remark', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Note"
              multiline
              rows={2}
              value={form.note}
              onChange={(e) => onFieldChange('note', e.target.value)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default OpportunityFormFields;
